import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OrcamentoService } from '../../services/orcamento.service';
import { FiltrarOrcamento } from '../../models/orcamento-filter.model';
import { OrcamentosListDto } from '../../models/orcamento-list-response.dto';
import { OrcamentoListItem } from '../../models/orcamento-list-item.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { PaginationDto } from 'src/app/core/dto/pagination.dto';
import { StatusOrcamentoEnum, StatusOrcamentoLabels } from '../../models/status-orcamento.enum';
import { AuthService } from 'src/app/core/services/auth/auth.service';

@Component({
  selector: 'app-orcamento-list',
  templateUrl: './orcamento-list.component.html',
  styleUrls: ['./orcamento-list.component.scss'],
  standalone: false
})
export class OrcamentoListComponent implements OnInit, OnDestroy {
  orcamentos: OrcamentoListItem[] = [];
  filtroForm: FormGroup;
  private readonly destroy$ = new Subject<void>();
  isLoading = false;
  error = '';
  erro403 = false;
  private atualizandoPaginaProgramaticamente = false;
  pagination: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 1
  };

  // Permissões
  podeVer = false;
  podeEditar = false;

  constructor(
    private orcamentoService: OrcamentoService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService
  ) {
    this.filtroForm = this.fb.group({
      PaginaAtual: [1],
      QuantidadePorPagina: [10],
      Cliente: [''],
      Projeto: ['']
    });
  }

  ngOnInit(): void {
    this.verificarPermissoes();
    this.configurarFiltros();
    this.carregarOrcamentos();
  }

  verificarPermissoes(): void {
    this.podeVer = this.authService.verificarPermissao('Doc_Orcamento', 'Ver');
    this.podeEditar = this.authService.verificarPermissao('Doc_Orcamento', 'Editar');
    
    if (!this.podeVer) {
      this.erro403 = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarOrcamentos(): void {
    const formValue = this.filtroForm.value;
    // API espera PaginaAtual como 0-based, mas formulário usa 1-based
    const paginaAtual = Math.max(1, formValue.PaginaAtual || 1);
    const paginaAtualApi = Math.max(0, paginaAtual - 1);
    
    // Normalizar valores dos filtros (trim para remover espaços)
    const cliente = (formValue.Cliente || '').trim();
    const projeto = (formValue.Projeto || '').trim();
    
    const filtros: FiltrarOrcamento = {
      paginaAtual: paginaAtualApi.toString(),
      quantidadePorPagina: formValue.QuantidadePorPagina?.toString() || '10'
    };
    
    // Só adicionar filtros se tiverem valor
    if (cliente) {
      filtros.Cliente = cliente;
    }
    
    if (projeto) {
      filtros.Projeto = projeto;
    }

    this.getOrcamentos(filtros);
  }

  getOrcamentos(filtros: FiltrarOrcamento): void {
    this.isLoading = true;
    this.error = '';

    this.orcamentoService
      .listarOrcamentos(filtros)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: OrcamentosListDto) => {
          this.orcamentos = response.orcamentos || [];
          // API retorna paginaAtual como 0-based, converter para 1-based para ngb-pagination
          const paginaAtualApi = Math.max(0, response.paginacao?.paginaAtual ?? 0);
          const novaPagina = Math.max(1, paginaAtualApi + 1);
          
          // Marcar que estamos atualizando programaticamente para evitar loop
          this.atualizandoPaginaProgramaticamente = true;
          
          // Atualizar paginação
          this.pagination = {
            paginaAtual: novaPagina,
            quantidadePorPagina: response.paginacao?.quantidadePorPagina || 10,
            totalDeItens: response.paginacao?.totalDeItens || 0,
            ultimaPagina: Math.max(1, response.paginacao?.ultimaPagina || 1)
          };
          
          // Sincronizar formulário sem disparar eventos
          if (this.filtroForm.get('PaginaAtual')?.value !== novaPagina) {
            this.filtroForm.patchValue({ PaginaAtual: novaPagina }, { emitEvent: false });
          }
          
          this.isLoading = false;
          
          // Resetar flag após um pequeno delay
          setTimeout(() => {
            this.atualizandoPaginaProgramaticamente = false;
          }, 100);
        },
        error: (error) => {
          if (error.status === 403) {
            this.erro403 = true;
            this.isLoading = false;
            return;
          }
          console.error('Erro ao carregar orçamentos:', error);
          this.error = 'Erro ao carregar dados dos orçamentos.';
          this.toastService.show('Erro ao consultar lista de orçamentos', {
            classname: 'bg-danger text-light',
          });
          this.isLoading = false;
        }
      });
  }

  private ultimaPaginaForm: number = 1;
  private ultimosFiltros: any = {};

  configurarFiltros(): void {
    this.ultimaPaginaForm = this.filtroForm.get('PaginaAtual')?.value || 1;
    this.ultimosFiltros = { ...this.filtroForm.value };
    
    // Escutar mudanças nos campos de filtro (Cliente e Projeto)
    this.filtroForm.get('Cliente')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.aplicarFiltros();
      });

    this.filtroForm.get('Projeto')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.aplicarFiltros();
      });

  }

  private aplicarFiltros(): void {
    const value = this.filtroForm.value;
    
    // Normalizar valores vazios para string vazia
    const clienteAtual = (value.Cliente || '').trim();
    const projetoAtual = (value.Projeto || '').trim();
    const clienteAnterior = (this.ultimosFiltros.Cliente || '').trim();
    const projetoAnterior = (this.ultimosFiltros.Projeto || '').trim();
    
    // Verificar se algum filtro mudou
    const filtrosMudaram = 
      clienteAtual !== clienteAnterior ||
      projetoAtual !== projetoAnterior ||
      value.QuantidadePorPagina !== this.ultimosFiltros.QuantidadePorPagina;
    
    // Se os filtros mudaram, resetar para página 1
    if (filtrosMudaram) {
      this.filtroForm.patchValue({ PaginaAtual: 1 }, { emitEvent: false });
      this.ultimaPaginaForm = 1;
    }
    
    // Atualizar últimos filtros com valores normalizados
    this.ultimosFiltros = {
      ...value,
      Cliente: clienteAtual,
      Projeto: projetoAtual
    };
    
    this.carregarOrcamentos();
  }

  limparFiltros(): void {
    this.ultimaPaginaForm = 1;
    this.filtroForm.patchValue({
      PaginaAtual: 1,
      QuantidadePorPagina: 10,
      Cliente: '',
      Projeto: ''
    }, { emitEvent: false });
    this.ultimosFiltros = {
      PaginaAtual: 1,
      QuantidadePorPagina: 10,
      Cliente: '',
      Projeto: ''
    };
    this.carregarOrcamentos();
  }

  onPageChange(page: number): void {
    if (this.atualizandoPaginaProgramaticamente) {
      return;
    }
    
    const paginaAtual = Math.max(1, page);
    this.ultimaPaginaForm = paginaAtual;
    this.ultimosFiltros = { ...this.filtroForm.value, PaginaAtual: paginaAtual };
    this.filtroForm.patchValue({
      PaginaAtual: paginaAtual
    }, { emitEvent: false });
    this.carregarOrcamentos();
  }

  onQuantidadePorPaginaChange(): void {
    this.ultimaPaginaForm = 1;
    this.ultimosFiltros = { ...this.filtroForm.value, PaginaAtual: 1, QuantidadePorPagina: this.filtroForm.get('QuantidadePorPagina')?.value };
    this.filtroForm.patchValue({
      PaginaAtual: 1
    }, { emitEvent: false });
    this.carregarOrcamentos();
  }

  novoOrcamento(): void {
    // Navega para criar um novo orçamento
    this.router.navigate(['/telemarketing/projetos/orcamento/novo']);
  }

  visualizarOrcamento(orcamento: OrcamentoListItem): void {
    if (orcamento.id) {
      this.router.navigate([`/telemarketing/projetos/orcamento/${orcamento.id}`]);
    }
  }

  formatarMoeda(valor: number | undefined | null): string {
    if (valor === undefined || valor === null || valor === 0) {
      return '-';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }

  formatarMedida(valor: number | undefined | null): string {
    if (valor === undefined || valor === null || valor === 0) {
      return '-';
    }
    return valor.toFixed(2).replace('.', ',');
  }

  formatarStatus(statusOrcamento: number | undefined | null): string {
    if (statusOrcamento === undefined || statusOrcamento === null) {
      return '-';
    }
    return StatusOrcamentoLabels[statusOrcamento as StatusOrcamentoEnum] || '-';
  }

  getStatusClass(statusOrcamento: number | undefined | null): string {
    if (statusOrcamento === undefined || statusOrcamento === null) {
      return 'badge bg-secondary';
    }
    switch (statusOrcamento) {
      case StatusOrcamentoEnum.Orcando:
        return 'badge bg-warning';
      case StatusOrcamentoEnum.Aprovado:
        return 'badge bg-success';
      case StatusOrcamentoEnum.Reprovado:
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  }
}

